import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import "./styles/Sales.css";

const PAYMENT_API = "https://salathai.co.tz/mrms/Payment.php";
const MENU_API = "https://salathai.co.tz/mrms/Menu.php";
const ACTUAL_API = "https://salathai.co.tz/mrms/Actual.php";

export default function AdminSalesReport() {
  const today = new Date().toISOString().split("T")[0];

  // Helper to get local date string without timezone conversion
  const getLocalDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get start of day (00:00:00) for a given date
  const getStartOfDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Get end of day (23:59:59.999) for a given date
  const getEndOfDay = (date) => {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actualBalance, setActualBalance] = useState(null);
  const [reportType, setReportType] = useState("daily");

  const token = localStorage.getItem("token");

  // Format date as DD-MMM-YY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Format date range for display
  const formatDateRange = (start, end) => {
    if (start === end) {
      return formatDate(start);
    }
    return `${formatDate(start)} to ${formatDate(end)}`;
  };

  // Fetch actual balance for selected date range (aggregated)
  const fetchActualBalances = async (startDateStr, endDateStr) => {
    if (!token) return null;

    try {
      // Fetch all actual balances in the date range
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      const dates = [];
      
      // Generate all dates in range
      let current = new Date(start);
      while (current <= end) {
        dates.push(getLocalDateString(current));
        current.setDate(current.getDate() + 1);
      }

      let totalCash = 0;
      let totalMpesa = 0;
      let totalCrdb = 0;
      let totalSelecom = 0;
      let totalMix = 0;
      let totalAmount = 0;
      let hasData = false;

      for (const dateStr of dates) {
        const url = `${ACTUAL_API}?action=check&date=${dateStr}&token=${token}`;
        const res = await fetch(url);
        const data = await res.json();
        
        if (data.exists && data.data) {
          hasData = true;
          totalCash += parseFloat(data.data.cash_amount) || 0;
          totalMpesa += parseFloat(data.data.mpesa_amount) || 0;
          totalCrdb += parseFloat(data.data.crdb_bank_amount) || 0;
          totalSelecom += parseFloat(data.data.selecom_amount) || 0;
          totalMix += parseFloat(data.data.mix_by_yas_amount) || 0;
          totalAmount += parseFloat(data.data.total_amount) || 0;
        }
      }

      if (hasData) {
        return {
          cash: totalCash,
          mpesa: totalMpesa,
          crdb: totalCrdb,
          selecom: totalSelecom,
          mix: totalMix,
          total: totalAmount
        };
      }
      return null;
    } catch (err) {
      console.error("Error fetching actual balances:", err);
      return null;
    }
  };

  const handleReportTypeChange = (type) => {
    setReportType(type);
    const todayDate = new Date();
    const localToday = getLocalDateString(todayDate);
    
    switch(type) {
      case "daily":
        setStartDate(localToday);
        setEndDate(localToday);
        break;
      case "weekly":
        const weekAgo = new Date(todayDate);
        weekAgo.setDate(todayDate.getDate() - 7);
        setStartDate(getLocalDateString(weekAgo));
        setEndDate(localToday);
        break;
      case "monthly":
        const monthAgo = new Date(todayDate);
        monthAgo.setMonth(todayDate.getMonth() - 1);
        setStartDate(getLocalDateString(monthAgo));
        setEndDate(localToday);
        break;
      default:
        break;
    }
  };

  const fetchReport = async () => {
    setLoading(true);

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };

      /* MENU */
      const menuRes = await fetch(MENU_API, { headers });
      const menu = await menuRes.json();

      /* PAYMENTS */
      const payRes = await fetch(PAYMENT_API, { headers });
      const payments = await payRes.json();

      // Create date range with full day coverage
      const startDateObj = getStartOfDay(startDate);
      const endDateObj = getEndOfDay(endDate);
      
      // Fetch actual balances for date range
      const actualBal = await fetchActualBalances(startDate, endDate);
      setActualBalance(actualBal);
      
      /* FILTER PAYMENTS - Date Range */
      const filteredPayments = payments.filter((p) => {
        if (!p.created_at) return false;
        if (p.status !== "PAID") return false;
        
        const paymentDate = new Date(p.created_at);
        
        // Compare if payment date is within the full day range (inclusive)
        const isAfterOrEqualStart = paymentDate >= startDateObj;
        const isBeforeOrEqualEnd = paymentDate <= endDateObj;
        
        return isAfterOrEqualStart && isBeforeOrEqualEnd;
      });

      if (filteredPayments.length === 0 && !actualBal) {
        setReport(null);
        alert(`No data found for ${formatDateRange(startDate, endDate)}. Please check if there are any paid orders for this date range.`);
        setLoading(false);
        return;
      }

      /* HOURLY - Group by hour across the date range */
      const hourlyMap = {};

      // Create hours from 5:00 to 4:00 (next day)
      for (let i = 5; i < 29; i++) {
        const hour24 = i % 24;
        const startHour = hour24.toString().padStart(2, "0") + ":00";
        const endHour = ((hour24 + 1) % 24).toString().padStart(2, "0") + ":00";
        const range = `${startHour}-${endHour}`;
        hourlyMap[range] = { range, items: 0, value: 0, avgPrice: 0 };
      }

      /* ITEMS MAP */
      const itemsMap = {};
      let totalItems = 0;

      /* CATEGORY MAP for summary */
      const categoryMap = {};

      /* STAFF PERFORMANCE MAP */
      const staffMap = {};

      /* PAYMENT METHOD MAP for summary */
      const paymentMethodMap = {
        "CASH": { method: "CASH", count: 0, total: 0, transactions: [] },
        "M-PESA": { method: "M-PESA", count: 0, total: 0, transactions: [] },
        "CRDB BANK": { method: "CRDB BANK", count: 0, total: 0, transactions: [] },
        "MIX BY YAS": { method: "MIX BY YAS", count: 0, total: 0, transactions: [] },
        "SELECOM": { method: "SELECOM", count: 0, total: 0, transactions: [] }
      };

      filteredPayments.forEach((payment) => {
        const paymentDate = new Date(payment.created_at);
        const paymentHour = paymentDate.getHours();
        
        // Find the correct hour range
        let foundRange = null;
        for (let i = 5; i < 29; i++) {
          const startHour = i % 24;
          const endHour = (i + 1) % 24;
          
          if (paymentHour === startHour) {
            const startStr = startHour.toString().padStart(2, "0") + ":00";
            const endStr = endHour.toString().padStart(2, "0") + ":00";
            foundRange = `${startStr}-${endStr}`;
            break;
          }
        }

        const qty = parseInt(payment.quantity_total) || 0;
        const amount = parseFloat(payment.total_amount) || 0;

        if (foundRange && hourlyMap[foundRange]) {
          hourlyMap[foundRange].items += qty;
          hourlyMap[foundRange].value += amount;
        }

        totalItems += qty;

        /* STAFF PERFORMANCE */
        const staffName = payment.staff_name || "Unknown";
        if (!staffMap[staffName]) {
          staffMap[staffName] = {
            name: staffName,
            orders: [],
            totalItems: 0,
            totalValue: 0,
          };
        }

        /* PAYMENT METHOD SUMMARY */
        const method = payment.payment_method || "Unknown";
        if (paymentMethodMap[method]) {
          paymentMethodMap[method].count++;
          paymentMethodMap[method].total += amount;
          paymentMethodMap[method].transactions.push({
            time: paymentDate.toTimeString().split(" ")[0],
            date: paymentDate.toISOString().split("T")[0],
            order: payment.order_number || payment.order_id || "N/A",
            amount: amount,
            staff: payment.staff_name || "Unknown",
            paymentStatus: payment.payment_status || "PAID"
          });
        } else if (method !== "Unknown") {
          if (!paymentMethodMap[method]) {
            paymentMethodMap[method] = { method, count: 0, total: 0, transactions: [] };
          }
          paymentMethodMap[method].count++;
          paymentMethodMap[method].total += amount;
          paymentMethodMap[method].transactions.push({
            time: paymentDate.toTimeString().split(" ")[0],
            date: paymentDate.toISOString().split("T")[0],
            order: payment.order_number || payment.order_id || "N/A",
            amount: amount,
            staff: payment.staff_name || "Unknown",
            paymentStatus: payment.payment_status || "PAID"
          });
        }

        /* PARSE ITEMS JSON */
        if (payment.items) {
          let items = [];

          try {
            items =
              typeof payment.items === "string"
                ? JSON.parse(payment.items)
                : payment.items;
          } catch (e) {}

          /* Add items to staff performance */
          items.forEach((item) => {
            const itemName = item.item_name;
            const quantity = parseInt(item.quantity) || 1;
            const price = parseFloat(item.price) || 0;

            staffMap[staffName].orders.push({
              orderId: payment.order_id || "N/A",
              orderNumber: payment.order_number || payment.order_id || "N/A",
              date: paymentDate.toISOString().split("T")[0],
              time: paymentDate.toLocaleTimeString(),
              itemName,
              quantity,
              price,
              paymentStatus: payment.payment_status || "PAID"
            });

            staffMap[staffName].totalItems += quantity;
            staffMap[staffName].totalValue += price * quantity;
          });

          /* Process items for itemsMap and categoryMap */
          items.forEach((item) => {
            const name = item.item_name;
            const quantity = parseInt(item.quantity) || 1;
            const price = parseFloat(item.price) || 0;
            const subtotal = price * quantity;

            // Find category from menu
            const menuItem = menu.find((m) => m.item_name === name);
            const category = menuItem?.group_button || "Uncategorized";

            // Category aggregation
            if (!categoryMap[category]) {
              categoryMap[category] = {
                category: category,
                qty: 0,
                total: 0
              };
            }
            categoryMap[category].qty += quantity;
            categoryMap[category].total += subtotal;

            if (!itemsMap[name]) {
              itemsMap[name] = {
                code: menuItem?.id || `ITEM${Object.keys(itemsMap).length + 1}`,
                name,
                qty: 0,
                total: 0,
                group: category,
              };
            }

            itemsMap[name].qty += quantity;
            itemsMap[name].total += subtotal;
          });
        }
      });

      // Calculate average prices for hourly data
      const hourlyWithSales = Object.values(hourlyMap)
        .filter(h => h.value > 0)
        .map(h => ({
          ...h,
          avgPrice: h.items > 0 ? h.value / h.items : 0
        }))
        .sort((a, b) => {
          const hourA = parseInt(a.range.split(":")[0]);
          const hourB = parseInt(b.range.split(":")[0]);
          return hourA - hourB;
        });

      // Calculate hourly totals
      const hourlyTotals = {
        totalItems: hourlyWithSales.reduce((sum, h) => sum + h.items, 0),
        totalValue: hourlyWithSales.reduce((sum, h) => sum + h.value, 0),
        overallAvgPrice: hourlyWithSales.reduce((sum, h) => sum + h.avgPrice, 0) / (hourlyWithSales.length || 1)
      };

      /* SORT ITEMS HIGH → LOW */
      const rankedItems = Object.values(itemsMap).sort((a, b) => b.qty - a.qty);

      /* Calculate items totals */
      const totalItemsQty = rankedItems.reduce((sum, item) => sum + item.qty, 0);
      const totalItemsAmount = rankedItems.reduce((sum, item) => sum + item.total, 0);

      /* Category Summary - sorted by category name */
      const categorySummary = Object.values(categoryMap)
        .sort((a, b) => a.category.localeCompare(b.category));

      const categoryTotals = {
        totalQty: categorySummary.reduce((sum, cat) => sum + cat.qty, 0),
        totalAmount: categorySummary.reduce((sum, cat) => sum + cat.total, 0)
      };

      /* SUMMARY */
      const totalSales = filteredPayments.reduce(
        (sum, p) => sum + (parseFloat(p.total_amount) || 0),
        0
      );

      // Calculate payment summary with actual and difference
      const paymentSummary = Object.values(paymentMethodMap)
        .filter(m => m.count > 0 || (actualBal && getActualForMethod(m.method, actualBal) > 0))
        .map(method => {
          const actualAmount = actualBal ? getActualForMethod(method.method, actualBal) : 0;
          const difference = method.total - actualAmount;
          return {
            ...method,
            actualAmount,
            difference
          };
        });

      // Calculate grand totals
      const grandTotalTransactions = paymentSummary.reduce((sum, m) => sum + m.count, 0);
      const grandTotalAmount = paymentSummary.reduce((sum, m) => sum + m.total, 0);
      const grandTotalActual = paymentSummary.reduce((sum, m) => sum + m.actualAmount, 0);
      const grandTotalDifference = grandTotalAmount - grandTotalActual;

      setReport({
        dateRange: { start: startDate, end: endDate },
        formattedDateRange: formatDateRange(startDate, endDate),
        hourly: hourlyWithSales,
        hourlyTotals: hourlyTotals,
        transactions: filteredPayments.map((p) => ({
          date: new Date(p.created_at).toISOString().split("T")[0],
          time: new Date(p.created_at).toTimeString().split(" ")[0],
          order: p.order_number || p.order_id || "N/A",
          amount: parseFloat(p.total_amount) || 0,
          method: p.payment_method || "Unknown",
          staff: p.staff_name || "Unknown",
          paymentStatus: p.payment_status || "PAID"
        })),
        items: rankedItems,
        itemsTotals: {
          totalQty: totalItemsQty,
          totalAmount: totalItemsAmount,
        },
        categorySummary: categorySummary,
        categoryTotals: categoryTotals,
        paymentMethods: Object.values(paymentMethodMap).filter(m => m.count > 0),
        paymentSummary: {
          methods: paymentSummary,
          grandTotal: {
            transactions: grandTotalTransactions,
            amount: grandTotalAmount,
            actual: grandTotalActual,
            difference: grandTotalDifference
          }
        },
        staffPerformance: Object.values(staffMap).sort((a, b) =>
          a.name.localeCompare(b.name)
        ),
        summary: {
          totalSales,
          totalOrders: filteredPayments.length,
          totalItems,
          totalTransactions: filteredPayments.length,
          averageOrderValue: filteredPayments.length > 0 ? totalSales / filteredPayments.length : 0
        },
        actualBalance: actualBal
      });
    } catch (err) {
      console.error(err);
      alert("Failed to load report");
    }

    setLoading(false);
  };

  // Helper function to get actual amount for a payment method
  const getActualForMethod = (method, actualBal) => {
    if (!actualBal) return 0;
    switch(method) {
      case "CASH": return actualBal.cash;
      case "M-PESA": return actualBal.mpesa;
      case "CRDB BANK": return actualBal.crdb;
      case "SELECOM": return actualBal.selecom;
      case "MIX BY YAS": return actualBal.mix;
      default: return 0;
    }
  };

  useEffect(() => {
    if (token) fetchReport();
  }, []);

  /* EXPORT EXCEL */
  const exportExcel = () => {
    if (!report) return;

    const wb = XLSX.utils.book_new();

    /* SUMMARY */
    const summarySheet = XLSX.utils.aoa_to_sheet([
      ["SALES REPORT"],
      [`Period: ${report.formattedDateRange}`],
      [`Generated: ${new Date().toLocaleString()}`],
      [],
      ["SUMMARY STATISTICS"],
      ["Total Sales (TZS)", report.summary.totalSales.toLocaleString()],
      ["Total Orders", report.summary.totalOrders],
      ["Items Sold", report.summary.totalItems],
      ["Average Order Value (TZS)", report.summary.averageOrderValue.toFixed(2)],
      ["Total Transactions", report.summary.totalTransactions],
    ]);

    XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

    /* HOURLY WITH AVERAGE PRICE */
    const hourlySheetData = [
      ["HOURLY SALES REPORT"],
      ["Hour Range", "Items", "Value (TZS)", "Avg Item Price (TZS)"],
      ...report.hourly.map((h) => [h.range, h.items, h.value, Math.round(h.avgPrice)]),
      [],
      ["TOTAL", report.hourlyTotals.totalItems, report.hourlyTotals.totalValue, Math.round(report.hourlyTotals.overallAvgPrice)]
    ];

    const hourlySheet = XLSX.utils.aoa_to_sheet(hourlySheetData);
    XLSX.utils.book_append_sheet(wb, hourlySheet, "Hourly");

    /* CATEGORY SUMMARY */
    const categorySheetData = [
      ["CATEGORY SUMMARY REPORT"],
      ["Category", "Quantity", "Total Amount (TZS)"],
      ...report.categorySummary.map((cat) => [cat.category, cat.qty, cat.total]),
      [],
      ["GRAND TOTAL", report.categoryTotals.totalQty, report.categoryTotals.totalAmount]
    ];

    const categorySheet = XLSX.utils.aoa_to_sheet(categorySheetData);
    XLSX.utils.book_append_sheet(wb, categorySheet, "Categories");

    /* PAYMENT SUMMARY WITH ACTUAL AND DIFFERENCE */
    const paymentSummaryData = [
      ["PAYMENT SUMMARY WITH ACTUAL BALANCE"],
      [`Period: ${report.formattedDateRange}`],
      [],
      ["Payment Method", "Transactions", "Total Amount (TZS)", "ACTUAL (TZS)", "DIFFERENCE (TZS)"],
      ...report.paymentSummary.methods.map((m) => [
        m.method,
        m.count,
        m.total,
        m.actualAmount,
        m.difference
      ]),
      [],
      ["GRAND TOTAL", 
        report.paymentSummary.grandTotal.transactions,
        report.paymentSummary.grandTotal.amount,
        report.paymentSummary.grandTotal.actual,
        report.paymentSummary.grandTotal.difference
      ]
    ];

    const paymentSummarySheet = XLSX.utils.aoa_to_sheet(paymentSummaryData);
    XLSX.utils.book_append_sheet(wb, paymentSummarySheet, "Payment Summary");

    /* PAYMENT METHODS DETAIL */
    const paymentSheetData = [["PAYMENT METHOD DETAILS"], []];

    report.paymentMethods.forEach((method) => {
      paymentSheetData.push([`${method.method} PAYMENTS`]);
      paymentSheetData.push(["Date", "Time", "Order", "Amount", "Staff", "Status"]);

      method.transactions.forEach((t) => {
        paymentSheetData.push([t.date, t.time, t.order, t.amount, t.staff, t.paymentStatus]);
      });

      paymentSheetData.push([
        `${method.method} Total`,
        "",
        "",
        method.total,
        `${method.count} transactions`,
        ""
      ]);
      paymentSheetData.push([]);
    });

    const paymentSheet = XLSX.utils.aoa_to_sheet(paymentSheetData);
    XLSX.utils.book_append_sheet(wb, paymentSheet, "Payments Detail");

    /* ITEMS */
    const itemSheetData = [
      ["ITEMIZED SALES REPORT"],
      ["Rank", "Code", "Item", "Category", "Qty", "Total"],
      ...report.items.map((i, index) => [
        index + 1,
        i.code,
        i.name,
        i.group,
        i.qty,
        i.total,
      ]),
      [],
      ["TOTALS", "", "", "", report.itemsTotals.totalQty, report.itemsTotals.totalAmount],
    ];

    const itemSheet = XLSX.utils.aoa_to_sheet(itemSheetData);
    XLSX.utils.book_append_sheet(wb, itemSheet, "Items");

    /* STAFF PERFORMANCE */
    const staffSheetData = [["STAFF PERFORMANCE REPORT"], [`Period: ${report.formattedDateRange}`], []];

    report.staffPerformance.forEach((staff) => {
      staffSheetData.push([`${staff.name.toUpperCase()}`]);
      staffSheetData.push(["Date", "Order ID", "Time", "Item", "Qty", "Price", "Status"]);

      staff.orders.forEach((order) => {
        staffSheetData.push([
          order.date || "",
          order.orderNumber,
          order.time,
          order.itemName,
          order.quantity,
          order.price,
          order.paymentStatus
        ]);
      });

      staffSheetData.push([
        `${staff.name} Summary`,
        "",
        "",
        `Total Items: ${staff.totalItems}`,
        "",
        `Total Value: ${staff.totalValue}`,
        ""
      ]);
      staffSheetData.push([]);
    });

    const staffSheet = XLSX.utils.aoa_to_sheet(staffSheetData);
    XLSX.utils.book_append_sheet(wb, staffSheet, "Staff Performance");

    XLSX.writeFile(wb, `Sales_Report_${startDate}_to_${endDate}.xlsx`);
  };

  return (
    <div className="report-page">
      <div className="report-header">
        <h1>Sales Report</h1>

        <div className="report-controls">
          <select 
            value={reportType} 
            onChange={(e) => handleReportTypeChange(e.target.value)}
            className="report-type-select"
          >
            <option value="daily">Daily Report</option>
            <option value="weekly">Weekly Report</option>
            <option value="monthly">Monthly Report</option>
            <option value="custom">Custom Range</option>
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="date-input"
          />

          <span className="date-separator">to</span>

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="date-input"
          />

          <button onClick={fetchReport} disabled={loading}>
            {loading ? "Loading..." : "Generate Report"}
          </button>

          <button onClick={exportExcel} disabled={!report}>
            Export Excel
          </button>
        </div>
      </div>

      {report && (
        <>
          {/* SUMMARY */}
          <section className="report-section">
            <h2>Sales Summary - {report.formattedDateRange}</h2>

            <div className="summary-cards">
              <div className="summary-card">
                <span>Total Sales</span>
                <strong>TZS {report.summary.totalSales.toLocaleString()}</strong>
              </div>

              <div className="summary-card">
                <span>Orders</span>
                <strong>{report.summary.totalOrders}</strong>
              </div>

              <div className="summary-card">
                <span>Items Sold</span>
                <strong>{report.summary.totalItems}</strong>
              </div>

              <div className="summary-card">
                <span>Avg Order</span>
                <strong>
                  TZS {report.summary.averageOrderValue.toFixed(0)}
                </strong>
              </div>
            </div>
          </section>

          {/* HOURLY REPORT WITH AVERAGE PRICE */}
          <section className="report-section">
            <h2>Hourly Sales Report</h2>

            <table className="report-table">
              <thead>
                <tr>
                  <th>Hour Range</th>
                  <th>Items</th>
                  <th>Value (TZS)</th>
                  <th>Avg Item Price (TZS)</th>
                </tr>
              </thead>

              <tbody>
                {report.hourly.map((h, i) => (
                  <tr key={i}>
                    <td>{h.range}</td>
                    <td>{h.items}</td>
                    <td>TZS {h.value.toLocaleString()}</td>
                    <td>TZS {Math.round(h.avgPrice).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  <td><strong>{report.hourlyTotals.totalItems}</strong></td>
                  <td><strong>TZS {report.hourlyTotals.totalValue.toLocaleString()}</strong></td>
                  <td><strong>TZS {Math.round(report.hourlyTotals.overallAvgPrice).toLocaleString()}</strong></td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* CATEGORY SUMMARY SECTION */}
          <section className="report-section">
            <h2>Category Summary Report</h2>

            <table className="report-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Quantity</th>
                  <th>Total Amount (TZS)</th>
                </tr>
              </thead>
              <tbody>
                {report.categorySummary.map((cat, idx) => (
                  <tr key={idx}>
                    <td className="category-name">{cat.category}</td>
                    <td>{cat.qty}</td>
                    <td>TZS {cat.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td><strong>GRAND TOTAL</strong></td>
                  <td><strong>{report.categoryTotals.totalQty}</strong></td>
                  <td><strong>TZS {report.categoryTotals.totalAmount.toLocaleString()}</strong></td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* PAYMENT SUMMARY WITH ACTUAL AND DIFFERENCE */}
          <section className="report-section">
            <h2>Payment Summary with Actual Balance</h2>
            
            {report.actualBalance ? (
              <div className="actual-balance-info">
                <span className="actual-badge">✓ Actual balance recorded for {report.formattedDateRange}</span>
              </div>
            ) : (
              <div className="actual-balance-warning">
                <span className="warning-badge">⚠ No actual balance recorded for this period</span>
              </div>
            )}

            <table className="report-table payment-summary-table">
              <thead>
                <tr>
                  <th>Payment Method</th>
                  <th>Transactions</th>
                  <th>Total Amount (TZS)</th>
                  <th>ACTUAL (TZS)</th>
                  <th>DIFFERENCE (TZS)</th>
                </tr>
              </thead>
              <tbody>
                {report.paymentSummary.methods.map((method, idx) => (
                  <tr key={idx}>
                    <td className="method-name">{method.method}</td>
                    <td>{method.count}</td>
                    <td>TZS {method.total.toLocaleString()}</td>
                    <td className={method.actualAmount > 0 ? "actual-positive" : "actual-zero"}>
                      TZS {method.actualAmount.toLocaleString()}
                    </td>
                    <td className={method.difference >= 0 ? "difference-positive" : "difference-negative"}>
                      TZS {method.difference.toLocaleString()}
                      {method.difference !== 0 && (
                        <span className="difference-percent">
                          ({((method.difference / (method.total || 1)) * 100).toFixed(1)}%)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="grand-total-row">
                  <td><strong>GRAND TOTAL</strong></td>
                  <td><strong>{report.paymentSummary.grandTotal.transactions}</strong></td>
                  <td><strong>TZS {report.paymentSummary.grandTotal.amount.toLocaleString()}</strong></td>
                  <td><strong>TZS {report.paymentSummary.grandTotal.actual.toLocaleString()}</strong></td>
                  <td className={report.paymentSummary.grandTotal.difference >= 0 ? "difference-positive" : "difference-negative"}>
                    <strong>TZS {report.paymentSummary.grandTotal.difference.toLocaleString()}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* PAYMENT METHODS DETAIL */}
          <section className="report-section">
            <h2>Payment Methods Detail</h2>

            {report.paymentMethods.map((method, idx) => (
              <div key={idx} className="payment-method-section">
                <h3>{method.method} Payments</h3>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Order</th>
                      <th>Amount</th>
                      <th>Staff</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {method.transactions.map((t, i) => (
                      <tr key={i}>
                        <td>{t.date}</td>
                        <td>{t.time}</td>
                        <td>{t.order}</td>
                        <td>TZS {t.amount.toLocaleString()}</td>
                        <td>{t.staff}</td>
                        <td>
                          <span className={`status-badge ${t.paymentStatus.toLowerCase()}`}>
                            {t.paymentStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3">
                        <strong>{method.method} Total:</strong>
                      </td>
                      <td>
                        <strong>TZS {method.total.toLocaleString()}</strong>
                      </td>
                      <td colSpan="2">
                        <strong>({method.count} transactions)</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </section>

          {/* ITEMIZED RANKING WITH TOTALS */}
          <section className="report-section">
            <h2>Itemized Sales Ranking</h2>

            <table className="report-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Code</th>
                  <th>Item</th>
                  <th>Category</th>
                  <th>Units Sold</th>
                  <th>Total Value</th>
                </tr>
              </thead>

              <tbody>
                {report.items.map((item, index) => (
                  <tr key={index}>
                    <td>#{index + 1}</td>
                    <td>{item.code}</td>
                    <td>{item.name}</td>
                    <td>{item.group}</td>
                    <td>{item.qty}</td>
                    <td>TZS {item.total.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td colSpan="4">
                    <strong>TOTALS</strong>
                  </td>
                  <td>
                    <strong>{report.itemsTotals.totalQty}</strong>
                  </td>
                  <td>
                    <strong>TZS {report.itemsTotals.totalAmount.toLocaleString()}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </section>

          {/* STAFF PERFORMANCE */}
          <section className="report-section">
            <h2>Staff Performance</h2>

            {report.staffPerformance.map((staff, idx) => (
              <div key={idx} className="staff-section">
                <h3>{staff.name}</h3>
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Order ID</th>
                      <th>Time</th>
                      <th>Item</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.orders.map((order, i) => (
                      <tr key={i}>
                        <td>{order.date || ""}</td>
                        <td>{order.orderNumber}</td>
                        <td>{order.time}</td>
                        <td>{order.itemName}</td>
                        <td>{order.quantity}</td>
                        <td>TZS {order.price.toLocaleString()}</td>
                        <td>
                          <span className={`status-badge ${order.paymentStatus.toLowerCase()}`}>
                            {order.paymentStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3">
                        <strong>{staff.name} Summary</strong>
                      </td>
                      <td>
                        <strong>Items: {staff.totalItems}</strong>
                      </td>
                      <td colSpan="3">
                        <strong>Total: TZS {staff.totalValue.toLocaleString()}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ))}
          </section>
        </>
      )}
    </div>
  );
}